import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { UserX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDeletePropertyResidentMutation } from '@@/services/property';
import SelectInput from '@@/components/Inputs/SelectInput';
import StandardInput from '@@/components/Inputs/StandardInput';

// "Je déménage" is not used on purpose: the API emails the neighbours of users
// deleted with that exact reason
const deletionReasons = [
  'Ne réside plus dans la résidence',
  "N'a jamais résidé dans la résidence",
  'Compte en double',
  'Autre',
].map((reason) => ({ id: reason, label: reason }));

type DeleteResidentFormType = {
  reason: string;
  comment?: string;
};

const schema = yup
  .object({
    reason: yup.string().required('Veuillez choisir une raison'),
    comment: yup.string().trim(),
  })
  .required();

const DeletePropertyResidentModal: React.FC<{
  propertyId: string;
  resident: Alacaza.Resident | null;
  onClose: () => void;
}> = ({ propertyId, resident, onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeleteResidentFormType>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const [deleteResident, { isLoading }] = useDeletePropertyResidentMutation();

  const closeModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  useEffect(() => {
    reset();
  }, [resident, reset]);

  const onSubmit = useCallback(
    async ({ reason, comment }: DeleteResidentFormType) => {
      if (!resident) {
        return;
      }

      try {
        await deleteResident({
          propertyId,
          userId: String(resident.id),
          reason,
          comment: comment || undefined,
        }).unwrap();
        toast.success('Le résident a bien été supprimé', {
          position: 'top-right',
        });
        closeModal();
      } catch (e) {
        const status = (e as { status?: number })?.status;
        toast.error(
          status === 409
            ? "Ce résident ne peut pas être supprimé : il est rattaché à une résidence que vous ne gérez pas, ou son compte vient d'être modifié."
            : 'Une erreur est survenue lors de la suppression du résident',
          { position: 'top-right' },
        );
      }
    },
    [resident, deleteResident, propertyId, closeModal],
  );

  return (
    <Modal
      isOpen={!!resident}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-6 sm:px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-4 right-4 sm:right-auto sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Delete property resident modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <UserX size={24} className="text-danger-600" />
        <h2 className="text-heading-md text-neutral-900">
          Supprimer un résident
        </h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      {!!resident && (
        <p className="text-body text-neutral-700 mb-6">
          Le compte de{' '}
          <span className="font-semibold">
            {resident.firstName} {resident.lastName}
          </span>{' '}
          sera supprimé. Il ne pourra plus se connecter à Alacaza.
        </p>
      )}

      <form className="space-y-6">
        <SelectInput
          id="reason"
          label="Raison de la suppression"
          placeholder="Choisir une raison"
          register={register}
          options={deletionReasons}
          error={errors.reason}
        />
        <StandardInput
          register={register}
          id="comment"
          label="Commentaire (facultatif)"
          type="textarea"
          placeholder="Commentaire"
          error={errors.comment}
        />
      </form>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button border border-neutral-300 text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 text-body-sm font-medium"
          onClick={closeModal}
        >
          Annuler
        </button>
        <button
          className="px-6 py-3 rounded-button bg-danger-600 hover:bg-danger-700 disabled:opacity-50 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          Supprimer
        </button>
      </div>
    </Modal>
  );
};

export default DeletePropertyResidentModal;
